/**
 * Select — le menu déroulant de la librairie, à la place du `<select>` natif
 * (sa liste est dessinée par l'OS : blanche, carrée, hors du thème — Marko,
 * 07-10, sur Helios : « les menus déroulants n'ont pas de CSS, ils sont
 * horribles »).
 *
 * Il s'écrit COMME un `<select>` : mêmes `<option>` / `<optgroup>`, même
 * `value`, même `onChange((e) => e.target.value)` — remplacer `<select` par
 * `<Select` suffit (78 fichiers du cockpit, 11 de Helios).
 *
 *   <Select etiquette="Langue" value={langue} onChange={(e) => setLangue(e.target.value)}>
 *     <optgroup label="Europe"><option value="fr">Français</option>…</optgroup>
 *   </Select>
 *
 * Le bouton est une surface de Champ (étiquette, aide, erreur, icône, tailles,
 * pilule). La liste s'ouvre sous lui — ou au-dessus si la place manque —,
 * portée sur `<body>` (au-dessus de tout, hors d'un panneau qui se ferme « au
 * clic dehors ») mais DANS le thème de l'endroit : elle recopie `.dark` et
 * `data-theme` de l'ancêtre du bouton. Coche de la marque sur le choix actif.
 * Clavier : ↑ ↓ Début Fin, Entrée ou Espace choisit, Échap ferme, une lettre
 * saute au choix qui commence par elle (fermé : elle choisit, comme le natif).
 *
 * Successeur de `apps/helios/src/select.tsx` (même contrat, même lecture des
 * enfants) ; le cockpit n'en avait aucun.
 */
import {
  Children,
  Fragment,
  isValidElement,
  useEffect,
  useId,
  useLayoutEffect,
  useRef,
  useState,
  type CSSProperties,
  type KeyboardEvent,
  type ReactNode,
  type Ref,
} from "react";
import { createPortal } from "react-dom";

import { SousLeChamp, TAILLES_CHAMP, type TailleChamp } from "./Champ";
import { Check, ChevronDown } from "./icons";
import { cadreDuTheme, rectVue, vueDuCorps, type CadreTheme } from "./portail";
import { cn } from "./utils";
import "./champs.css";

export interface ChoixSelect {
  valeur: string;
  etiquette: string;
  desactive?: boolean;
  groupe?: string;
}

/** Ce que `onChange` reçoit : la forme qu'un `<select>` donne, et que les appelants lisent (`e.target.value`). */
export interface EvenementSelect {
  target: { value: string };
  currentTarget: { value: string };
}

export interface SelectProps {
  value?: string | number | readonly string[];
  onChange?: (e: EvenementSelect) => void;
  /** `<option>` et `<optgroup>`, comme dans un `<select>`. Une option `hidden` n'est pas listée. */
  children?: ReactNode;
  etiquette?: ReactNode;
  aide?: ReactNode;
  erreur?: ReactNode;
  icone?: ReactNode;
  taille?: TailleChamp;
  pilule?: boolean;
  /** Quand aucune option ne porte `value` : ce texte, en gris. */
  placeholder?: string;
  disabled?: boolean;
  /** Dans un `<form>` : un `<input hidden>` porte la valeur sous ce nom. */
  name?: string;
  id?: string;
  title?: string;
  "aria-label"?: string;
  /** La classe du CADRE (largeur, marges). */
  className?: string;
}

const texteDe = (n: ReactNode): string =>
  n === null || n === undefined || typeof n === "boolean"
    ? ""
    : typeof n === "string" || typeof n === "number"
      ? String(n)
      : Array.isArray(n)
        ? n.map(texteDe).join("")
        : isValidElement(n)
          ? texteDe((n.props as { children?: ReactNode }).children)
          : "";

/** Les `<option>` / `<optgroup>` d'un `<select>`, lus comme le navigateur les lirait. */
export function lireLesOptions(children: ReactNode, groupe?: string, sortie: ChoixSelect[] = []): ChoixSelect[] {
  Children.forEach(children, (c) => {
    if (!isValidElement(c)) return;
    const p = c.props as { value?: string | number; children?: ReactNode; disabled?: boolean; label?: string; hidden?: boolean };
    if (c.type === Fragment) lireLesOptions(p.children, groupe, sortie);
    else if (c.type === "optgroup") lireLesOptions(p.children, p.label, sortie);
    else if (c.type === "option" && !p.hidden) {
      const etiquette = texteDe(p.children);
      sortie.push({ valeur: p.value !== undefined ? String(p.value) : etiquette, etiquette, desactive: p.disabled, groupe });
    }
  });
  return sortie;
}

/** Le prochain choix qu'on peut prendre, à partir de `depuis` (exclu), dans un sens — en boucle. */
function prochain(options: ChoixSelect[], depuis: number, sens: 1 | -1): number {
  const n = options.length;
  for (let k = 1; k <= n; k++) {
    const i = (depuis + sens * k + n * k) % n;
    if (!options[i].desactive) return i;
  }
  return -1;
}

/** Le prochain choix dont l'étiquette commence par `lettre`, après `depuis`. */
function chercher(options: ChoixSelect[], depuis: number, lettre: string): number {
  const l = lettre.toLowerCase();
  const n = options.length;
  for (let k = 1; k <= n; k++) {
    const i = (depuis + k) % n;
    if (!options[i].desactive && options[i].etiquette.toLowerCase().startsWith(l)) return i;
  }
  return -1;
}

export function Select({
  value,
  onChange,
  children,
  etiquette,
  aide,
  erreur,
  icone,
  taille = "md",
  pilule = false,
  placeholder,
  disabled,
  name,
  id: idDonne,
  title,
  "aria-label": ariaLabel,
  className,
}: SelectProps) {
  const genere = useId();
  const id = idDonne ?? genere;
  const idAide = `${id}-aide`;
  const idListe = `${id}-liste`;
  const t = TAILLES_CHAMP[taille];

  const options = lireLesOptions(children);
  const v = value === undefined ? "" : String(value);
  const indexActuel = options.findIndex((o) => o.valeur === v);
  const courant = indexActuel >= 0 ? options[indexActuel] : undefined;

  const bouton = useRef<HTMLButtonElement>(null);
  const liste = useRef<HTMLDivElement>(null);
  const [ouvert, setOuvert] = useState(false);
  const [vise, setVise] = useState(-1);
  const [cadre, setCadre] = useState<CadreTheme>({ sombre: false });
  const [pos, setPos] = useState<{ left: number; top: number; minWidth: number; sens: "bas" | "haut" } | null>(null);

  function ouvrir() {
    if (disabled || options.length === 0) return;
    setCadre(cadreDuTheme(bouton.current));
    setVise(indexActuel >= 0 && !options[indexActuel].desactive ? indexActuel : prochain(options, -1, 1));
    setPos(null);
    setOuvert(true);
  }
  function fermer() {
    setOuvert(false);
  }
  function choisir(valeur: string) {
    onChange?.({ target: { value: valeur }, currentTarget: { value: valeur } });
    fermer();
    bouton.current?.focus();
  }

  // Sous le bouton, à sa largeur ; au-dessus si la place manque en bas ; jamais hors de l'écran.
  useLayoutEffect(() => {
    if (!ouvert) return;
    const { zoom, largeur: W, hauteur: H } = vueDuCorps();
    const b = rectVue(bouton.current, zoom);
    const l = rectVue(liste.current, zoom);
    if (!b || !l) return;
    const marge = 8;
    const h = l.height;
    const w = Math.max(l.width, b.width);
    let sens: "bas" | "haut" = "bas";
    let top = b.bottom + 4;
    if (top + h > H - marge && b.top - 4 - h >= marge) {
      sens = "haut";
      top = b.top - 4 - h;
    }
    const left = Math.max(marge, Math.min(b.left, W - w - marge));
    setPos({ left, top: Math.max(marge, top), minWidth: b.width, sens });
  }, [ouvert]);

  // Un clic ailleurs, un défilement ailleurs, un redimensionnement, la fenêtre qui perd le focus : fermé.
  useEffect(() => {
    if (!ouvert) return;
    const dedans = (cible: EventTarget | null) =>
      cible instanceof Node && (liste.current?.contains(cible) || bouton.current?.contains(cible));
    const auClic = (e: PointerEvent) => {
      if (!dedans(e.target)) fermer();
    };
    const auDefilement = (e: Event) => {
      if (!(e.target instanceof Node && liste.current?.contains(e.target))) fermer();
    };
    window.addEventListener("pointerdown", auClic, true);
    window.addEventListener("scroll", auDefilement, true);
    window.addEventListener("resize", fermer);
    window.addEventListener("blur", fermer);
    return () => {
      window.removeEventListener("pointerdown", auClic, true);
      window.removeEventListener("scroll", auDefilement, true);
      window.removeEventListener("resize", fermer);
      window.removeEventListener("blur", fermer);
    };
  }, [ouvert]);

  // Le choix visé reste en vue quand la liste défile.
  useEffect(() => {
    if (!ouvert || vise < 0) return;
    const el = document.getElementById(`${idListe}-${vise}`);
    el?.scrollIntoView?.({ block: "nearest" });
  }, [ouvert, vise, idListe]);

  function auClavier(e: KeyboardEvent<HTMLButtonElement>) {
    if (disabled) return;
    const k = e.key;
    if (!ouvert) {
      if (k === "ArrowDown" || k === "ArrowUp" || k === "Enter" || k === " ") {
        e.preventDefault();
        ouvrir();
      } else if (k.length === 1 && /\S/.test(k)) {
        // Fermé, une lettre CHOISIT — comme un <select> natif.
        const i = chercher(options, indexActuel, k);
        if (i >= 0) choisir(options[i].valeur);
      }
      return;
    }
    switch (k) {
      case "ArrowDown":
        e.preventDefault();
        setVise(prochain(options, vise, 1));
        break;
      case "ArrowUp":
        e.preventDefault();
        setVise(prochain(options, vise, -1));
        break;
      case "Home":
        e.preventDefault();
        setVise(prochain(options, -1, 1));
        break;
      case "End":
        e.preventDefault();
        setVise(prochain(options, 0, -1));
        break;
      case "Enter":
      case " ":
        e.preventDefault();
        if (vise >= 0 && !options[vise].desactive) choisir(options[vise].valeur);
        else fermer();
        break;
      case "Escape":
        e.preventDefault();
        fermer();
        break;
      case "Tab":
        fermer();
        break;
      default:
        if (k.length === 1 && /\S/.test(k)) {
          const i = chercher(options, vise, k);
          if (i >= 0) setVise(i);
        }
    }
  }

  return (
    <div className={cn("flex min-w-0 flex-col gap-1.5", className)}>
      {etiquette !== undefined && (
        <label htmlFor={id} className="ch-etiquette">
          {etiquette}
        </label>
      )}
      <button
        ref={bouton}
        type="button"
        id={id}
        role="combobox"
        aria-haspopup="listbox"
        aria-expanded={ouvert}
        aria-controls={ouvert ? idListe : undefined}
        aria-activedescendant={ouvert && vise >= 0 ? `${idListe}-${vise}` : undefined}
        aria-label={ariaLabel}
        aria-invalid={erreur ? true : undefined}
        aria-describedby={(erreur ?? aide) !== undefined ? idAide : undefined}
        title={title}
        disabled={disabled}
        data-ouvert={ouvert || undefined}
        data-eteint={disabled || undefined}
        data-erreur={erreur ? true : undefined}
        className={cn("ch-surface flex w-full items-center text-left", pilule ? "ch-pilule" : t.arrondi, t.cadre, t.hauteur, !disabled && "cursor-pointer")}
        onClick={() => (ouvert ? fermer() : ouvrir())}
        onKeyDown={auClavier}
      >
        {icone !== undefined && <span className="ch-icone shrink-0 text-faint">{icone}</span>}
        {/* Une valeur qui n'est dans aucune option (une donnée d'avant la liste, une option retirée) se
            montre telle quelle plutôt qu'un champ vide ; `data-hors-liste` pour qui veut la voir. */}
        <span className={cn("min-w-0 flex-1 truncate", !courant && v === "" && "text-faint")} data-hors-liste={!courant && v !== "" ? true : undefined}>
          {courant?.etiquette ?? (v !== "" ? v : (placeholder ?? ""))}
        </span>
        <ChevronDown className={cn("ch-chevron text-faint", ouvert && "rotate-180")} aria-hidden />
      </button>
      {name !== undefined && <input type="hidden" name={name} value={v} />}
      <SousLeChamp id={idAide} aide={aide} erreur={erreur} />
      {ouvert &&
        createPortal(
          <div className={cadre.sombre ? "dark" : undefined} data-theme={cadre.theme}>
            <ListeDeChoix
              ref={liste}
              id={idListe}
              options={options}
              valeur={v}
              vise={vise}
              onViser={setVise}
              onChoisir={choisir}
              sens={pos?.sens}
              style={pos ? { left: pos.left, top: pos.top, minWidth: pos.minWidth } : { left: 0, top: 0, visibility: "hidden" }}
            />
          </div>,
          document.body,
        )}
    </div>
  );
}

/** La liste seule — ce que le Select ouvre. `statique` la pose dans la page (le catalogue, qui ne clique pas). */
export function ListeDeChoix({
  ref,
  id,
  options,
  valeur,
  vise,
  onViser,
  onChoisir,
  sens,
  statique = false,
  style,
  className,
}: {
  ref?: Ref<HTMLDivElement>;
  id?: string;
  options: ChoixSelect[];
  valeur: string;
  vise: number;
  onViser: (i: number) => void;
  onChoisir: (valeur: string) => void;
  sens?: "bas" | "haut";
  statique?: boolean;
  style?: CSSProperties;
  className?: string;
}) {
  return (
    <div
      ref={ref}
      id={id}
      role="listbox"
      data-sens={sens}
      className={cn("ch-menu ch-arrondi-menu", statique && "ch-menu-statique", className)}
      style={style}
    >
      {options.map((o, i) => {
        const nouveauGroupe = o.groupe !== undefined && (i === 0 || options[i - 1].groupe !== o.groupe);
        return (
          <Fragment key={`${o.valeur}-${i}`}>
            {nouveauGroupe && i > 0 && <div className="ch-sep" role="separator" />}
            {nouveauGroupe && (
              <div className="ch-groupe ch-etiquette" role="presentation">
                {o.groupe}
              </div>
            )}
            <div
              id={id ? `${id}-${i}` : undefined}
              role="option"
              aria-selected={o.valeur === valeur}
              aria-disabled={o.desactive || undefined}
              data-vise={i === vise || undefined}
              className="ch-choix"
              onPointerMove={() => {
                if (i !== vise) onViser(i);
              }}
              onPointerDown={(e) => e.preventDefault()}
              onClick={() => {
                if (!o.desactive) onChoisir(o.valeur);
              }}
            >
              <span className="min-w-0 flex-1 truncate">{o.etiquette}</span>
              {o.valeur === valeur && <Check className="ch-choix-coche" weight="bold" aria-hidden />}
            </div>
          </Fragment>
        );
      })}
    </div>
  );
}
